import {Utility} from "./utility";
import {SFV} from "./single-file-view";
import {Breadcrumbs} from "./breadcrumbs";
import {Features} from "./api";
import {errorHandler} from "../errors/errorHandler";

export const router = {

    /**
     * @type {{path: String, handler: String, meta: Object params: *}}
     */
    currentRoute: {
        path: '',
        handler: '',
        meta: {},
        params: {},
        animation: '',
    },

    /**
     * @type {{isNavBack: Boolean}}
     */
    options: {
        isNavBack: false
    },

    /**
     * Stores all the life cycle methods the user wants to use
     * @type {{before: Boolean|Function}}
     */
    lifeCycles: {
        before: false,
    },

    /**
     * @type {Object[]}
     */
    loadedRoutes: [],

    /**
     * the initial point for the router
      * @param routes
     */
    generateRouter: (routes) => {
        // add in the router events
        router.addRouterEvents();

        // generate the routes and params for the routes
        router.generateRoutes(routes, async () => {
            // if (!Utility.isBaseURL()) await router.getCurrentURL();
        });
    },

    /**
     * Generates the routes for the router to use
     * @param routes
     * @param finish
     */
    generateRoutes: (routes, finish) => {
        routes.forEach(route => {

            let builtRoute = {};

            //* check if route requires params
            if (route.path.includes(":")) {

                //* get the params required
                let params = route.path.split(":");

                //* remove the path from array
                let path = params.splice(0, 1)[0];

                //* build up a required params object for this route
                let required = {};
                params.forEach(param => {

                    //* check for defaults
                    if (param.includes("=")) {

                        //* get values
                        const defaultedVals = param.split("=");
                        const key = defaultedVals[0];
                        const value = defaultedVals[1];

                        if (value.includes("true") || value.includes("false")) {
                            if (value.includes("true")) {
                                required[key] = Boolean(value);
                            } else {
                                required[key] = Boolean(!value);
                            }
                        } else {
                            if (isNaN(Number(value))) {
                                required[key] = value;
                            } else if (!isNaN(Number(value))) {
                                required[key] = Number(value);
                            } else {
                                required[key] = value;
                            }
                        }
                    } else {
                        required[param] = false;
                    }
                });

                //* setup route object with newly built up params
                builtRoute["path"] = path;
                builtRoute["params"] = required;
                builtRoute["handler"] = route.handler;
                builtRoute["animate"] = route.animate;
                builtRoute["meta"] = route.meta;

            }
            //* route doesn't need params
            else {
                builtRoute["path"] = route.path;
                builtRoute["params"] = false;
                builtRoute["handler"] = route.handler;
                builtRoute["animate"] = route.animate;
                builtRoute["meta"] = route.meta;
            }

            router.loadedRoutes.push(builtRoute);

        });

        finish();
    },

    /**
     * Will get the current url and try and route to the page being requested
     * @returns {Promise<void>}
     */
    getCurrentURL: async () => {
        if (window.location.hash) {
            const url = window.location.hash.split("#")[1];
            const urlParams = window.location.hash.split("?")[1];
            const RAW_PARAMS = decodeURIComponent(urlParams).replace("?", "").split(",");

            let dataForRoute = {};
            if (RAW_PARAMS[0] !== "" && RAW_PARAMS[0] !== "undefined") {
                RAW_PARAMS.forEach((param, index) => {
                    let paramKey = param.split("=")[0];
                    let paramData = param.split("=")[1];
                    if (paramData.includes("true") || paramData.includes("false")) {
                        if (paramData.includes("true")) {
                            dataForRoute[paramKey] = Boolean(paramData);
                        } else {
                            dataForRoute[paramKey] = Boolean(!paramData);
                        }
                    } else {
                        if (isNaN(Number(paramData))) {
                            dataForRoute[paramKey] = paramData;
                        } else if (!isNaN(Number(paramData))) {
                            dataForRoute[paramKey] = Number(paramData);
                        } else {
                            dataForRoute[paramKey] = paramData;
                        }
                    }
                });
            }

            if (RAW_PARAMS.length > 0 && RAW_PARAMS[0] !== "") {
                if (router.currentRoute.path !== url.split("?")[0]) await router.search(url.split("?")[0], dataForRoute);
            } else {
                if (url.indexOf('?') !== -1) await router.search(url.split("?")[0], null);
                else if (router.currentRoute.path !== url) await router.search(url, null);
            }
        }
    },

    /**
     * Will attempt to load the route you asked for
     * @param path
     * @param params
     * @param isNavBack
     * @param isLifeCycle
     * @returns {Promise<void>}
     */
    search: async (path, params=null, isNavBack=false, isLifeCycle=false) => {
        // check that you are not trying to route to the current route you are on
        if (path !== router.currentRoute.path || params !== router.currentRoute.params) {
            const found = router.loadedRoutes.filter(route => route.path === path)[0];
            if (found) {
                // tell router what you are performing
                router.options.isNavBack = isNavBack;

                // add route to breadcrumbs
                if (isNavBack === false) Breadcrumbs.Add(found, (params !== null)?params:found.params);

                // if you are going back, set the current route to what you have gone back to
                if (isNavBack) {
                    found.params = (params !== null)?params:found.params;
                    router.currentRoute = found;
                }

                // check if an onDestroyed event was setup for previous view
                if (Features.LifeCycles["Destroyed"]) {
                    Features.LifeCycles["Destroyed"]();
                    Features.LifeCycles["Destroyed"] = false;
                }

                // build route method
                const route = async () => {
                    // build the url
                    router.buildUrl(path, (params !== null)?params:found.params);

                    // render the route
                    await SFV.Initialize(found.handler, (params)?params:found.params);
                };

                // check for beforeEach lifecycle method
                if (router.lifeCycles.before !== false && isLifeCycle === false) router.useBeforeEachMethod(found, router.currentRoute, route);
                else await route();
            }
            else {
                // get the template
                $ActiveJS['Template'] = Utility.convertTextToDocument(Utility.getDefault404HTML());

                // show a 404 page
                await SFV.startRenderProcess();

                // log router error
                errorHandler.New({
                    label: 'Router Error',
                    message: `Failed to load route (${path}). There are no loaded routes with this path name`
                });
            }
        }
        else {
            errorHandler.New({
                label: 'Router Error',
                message: `request to route to current view (${router.currentRoute.path}) denied`
            });
        }
    },

    /**
     * Will route to the previous route
     */
    navBack: async () => {
        const last = Breadcrumbs.getLast();
        if (last && (last.path !== router.currentRoute.path)) await router.search(last.path, last.params, true);
    },

    /**
     * Builds up the url with the params passed to a route
     * @param path
     * @param params
     */
    buildUrl: (path, params) => {

        let URL = path + "?";
        Object.entries(params).forEach(param => {
            let paramKey = param[0];
            let paramData = param[1];

            if (paramKey !== "__isProxy") {
                URL += `${paramKey}=${paramData},`;
            }
        });

        if (URL[URL.length - 1] === ",") {
            URL = URL.replace(/(^,)|(,$)/g, "");
        }

        window.location.hash = URL;

    },

    /**
     * Adds all events for the router
     */
    addRouterEvents: () => {
        window.onhashchange = async () => {
            const hash = window.location.hash.split('?')[0].replace('#', '');
            if ((hash !== router.currentRoute.path) && (router.options.isNavBack === false)) await router.getCurrentURL();
        }
    },

    /**
     * Router lifecycle method to allow user to tap into router just before a route occurs
     * @param {Function|Boolean} callback
     */
    setupBeforeEach: (callback = false) => {

        router.lifeCycles.before = callback;

    },

    /**
     * Executes the before each lifecycle method
     * @param {{path: String, handler: String, meta: Object, params: Object, animation: String}} to
     * @param {{path: String, handler: String, meta: Object, params: *}} from
     * @param {Function} action
     */
    useBeforeEachMethod: (to, from, action) => {
        router.lifeCycles.before(to, from, (options) => {
                //* setup the options
                const data = options || null;

                // if options were passed
                if (data !== null) {
                    // if an error was passed
                    if (data.error) {
                        // if redirect path is passed
                        if (data.path !== undefined) {
                            // if params are passed
                            if (data.params !== undefined) router.search(data.path, data.params, false, true);
                            else router.search(data.path, null, false, true);
                        }
                    }
                    // carry on with default route action
                    else action();
                }
                // carry on with default route action
                else action();
            });
    },

};